import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PulseLoader from "react-spinners/PulseLoader";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getDataWithToken } from "../../services/GetDataService";
import { handlePostData } from "../../services/PostDataService";
import BarcodeScanner from "../../components/BarcodeScanner";
import { customTextField } from "./technicianStyles";

const attachRepairSkus = async (
  repairId,
  skus,
  warrantyClaimedSku,
  warrantyId,
) => {
  const data = {
    repair_id: repairId,
    is_warranty_claimed_sku: warrantyClaimedSku,
    sku_numbers: skus.map((item) => item.sku_number),
  };

  if (warrantyClaimedSku) {
    if (warrantyId) {
      data.warranty_id = warrantyId;
    }
    data.claimed_on_sku_data = skus.map((item) => ({
      sku_number: item.sku_number,
      claimed_on_sku_number: parseInt(item.claimed_on_sku_number, 10),
    }));
  }

  return handlePostData("/api/v1/repairAttachedSpareparts/create", data, false);
};

const SparepartsForm = ({
  repair,
  token,
  myBranchId,
  logout,
  onNotify,
  onRepairUpdated,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const [claimedOnSkuSearch, setClaimedOnSkuSearch] = useState("");
  const [skuList, setSkuList] = useState([]);
  const [isWarrantyClaimedSku, setIsWarrantyClaimedSku] = useState(false);
  const [warrantyList, setWarrantyList] = useState([]);
  const [selectedWarrantyId, setSelectedWarrantyId] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState("sku");
  const [skuLoading, setSkuLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    setSkuSearch("");
    setClaimedOnSkuSearch("");
    setSkuList([]);
    setIsWarrantyClaimedSku(false);
    setWarrantyList([]);
    setSelectedWarrantyId("");
    setScanOpen(false);
    setScanTarget("sku");
  }, [repair?._id]);

  useEffect(() => {
    const fetchWarranties = async () => {
      if (!repair?._id) {
        setWarrantyList([]);
        setSelectedWarrantyId("");
        return;
      }

      const url = `/api/v1/warranty?repair_id=${repair._id}`;
      const response = await getDataWithToken(url, token);

      if (response?.status === 401) {
        logout();
        return;
      }

      if (response?.status >= 200 && response?.status < 300) {
        const warranties = response?.data?.data || [];
        setWarrantyList(warranties);
        // setSelectedWarrantyId(warranties[0]?._id || "");
        return;
      }

      setWarrantyList([]);
      setSelectedWarrantyId("");
    };

    fetchWarranties();
  }, [repair?._id]);

  const fetchStockBySku = async (skuNumber) => {
    const url = `/api/v1/stock?sku_number=${parseInt(skuNumber, 10)}`;
    const response = await getDataWithToken(url, token);

    if (response?.status === 401) {
      logout();
      return null;
    }

    return response;
  };

  const getStockItemFromBackend = async (skuNumber, notFoundMessage) => {
    const response = await fetchStockBySku(skuNumber);

    if (!response) {
      return null;
    }

    if (!(response?.status >= 200 && response?.status < 300)) {
      onNotify(
        response?.data?.message?.toString() || "Failed to search SKU",
        "error",
      );
      return null;
    }

    const stockItems = response?.data?.data || [];
    if (stockItems.length < 1) {
      onNotify(notFoundMessage, "error");
      return null;
    }

    return stockItems[0];
  };

  const getProductBySku = async (skuValue, claimedOnSkuValue = "") => {
    const sku = String(skuValue || "").trim();
    if (!sku) {
      onNotify("Please enter a SKU", "error");
      return;
    }

    const claimedOnSku = String(claimedOnSkuValue || "").trim();
    if (isWarrantyClaimedSku) {
      if (!claimedOnSku || Number.isNaN(parseInt(claimedOnSku, 10))) {
        onNotify("Please add claimed on SKU number", "error");
        return;
      }

      if (parseInt(sku, 10) === parseInt(claimedOnSku, 10)) {
        onNotify("SKU and claimed on SKU number cannot be the same", "error");
        return;
      }
    }

    setSkuLoading(true);

    const stockItem = isWarrantyClaimedSku
      ? await getStockItemFromBackend(
          claimedOnSku,
          "No product found for claimed on SKU number",
        )
      : await getStockItemFromBackend(sku, "No product found");

    if (!stockItem) {
      setSkuLoading(false);
      return;
    }

    const isSkuPresent = skuList.some(
      (product) =>
        parseInt(product.sku_number, 10) ===
        parseInt(stockItem?.sku_number, 10),
    );

    if (!isSkuPresent) {
      if (stockItem?.branch_id !== myBranchId) {
        onNotify("This is not your branch product", "error");
      } else if (stockItem?.stock_status === "Available") {
        setSkuList((prev) => [
          {
            ...stockItem,
            note: "",
            claimed_on_sku_number: isWarrantyClaimedSku ? sku : "",
          },
          ...prev,
        ]);
        setSkuSearch("");
        if (isWarrantyClaimedSku) {
          setClaimedOnSkuSearch("");
        }
      } else {
        onNotify("This product is not available for attached", "error");
      }
    } else {
      onNotify("This product is already in the list", "error");
    }

    setSkuLoading(false);
  };

  const handleAddSku = async () => {
    if (isWarrantyClaimedSku) {
      await getProductBySku(skuSearch, claimedOnSkuSearch);
      return;
    }

    await getProductBySku(skuSearch);
  };

  const openScan = (target) => {
    setScanTarget(target);
    setScanOpen(true);
  };

  const handleScan = (barcode) => {
    const value = String(barcode || "").replace(/\D/g, "");
    setScanOpen(false);

    if (scanTarget === "claimed") {
      setClaimedOnSkuSearch(value);
      return;
    }

    setSkuSearch(value);
    if (!isWarrantyClaimedSku) {
      getProductBySku(barcode, claimedOnSkuSearch);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!repair?._id) {
      onNotify("Please search a repair first", "error");
      return;
    }

    if (skuList.length < 1) {
      onNotify("Please add at least one SKU", "error");
      return;
    }

    if (isWarrantyClaimedSku && warrantyList.length > 0 && !selectedWarrantyId) {
      onNotify("Please select a warranty no", "error");
      return;
    }

    setUpdateLoading(true);

    const skuResponse = await attachRepairSkus(
      repair._id,
      skuList,
      isWarrantyClaimedSku,
      selectedWarrantyId,
    );

    if (skuResponse?.status === 401) {
      logout();
      return;
    }

    if (!(skuResponse?.status >= 200 && skuResponse?.status < 300)) {
      setUpdateLoading(false);
      onNotify(
        skuResponse?.data?.message?.toString() || "SKU attach failed",
        "error",
      );
      return;
    }

    onNotify("Added successfully", "success");
    setSkuList([]);
    setIsWarrantyClaimedSku(false);
    setClaimedOnSkuSearch("");

    if (repair?.repair_id) {
      const refreshed = await getDataWithToken(
        `/api/v1/repair/find?repair_id=${encodeURIComponent(repair.repair_id)}`,
        token,
      );
      if (refreshed?.status >= 200 && refreshed?.status < 300) {
        const payload = refreshed?.data?.data;
        const foundRepair = Array.isArray(payload) ? payload[0] : payload;
        if (foundRepair?._id || foundRepair?.repair_id) {
          onRepairUpdated(foundRepair);
        }
      }
    }

    setUpdateLoading(false);
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: "left" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isWarrantyClaimedSku}
              onChange={(e) => {
                const checked = e.target.checked;
                if (skuList.length > 0) {
                  setSkuList([]);
                }
                setSkuSearch("");
                setClaimedOnSkuSearch("");
                setIsWarrantyClaimedSku(checked);
                if (!checked) {
                  // setSelectedWarrantyId(warrantyList[0]?._id || "");
                }
              }}
              size="small"
            />
          }
          label="Warranty Claimed SKU"
          sx={{
            mb: 1.5,
            mx: 0,
            width: "100%",
            justifyContent: "flex-start",
            "& .MuiFormControlLabel-label": {
              fontSize: 14,
              color: "#0F1624",
            },
          }}
        />

        {isWarrantyClaimedSku && warrantyList.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel id="technician-warranty-no-label">Warranty No</InputLabel>
            <Select
              labelId="technician-warranty-no-label"
              id="technician-warranty-no"
              label="Warranty No"
              value={selectedWarrantyId}
              onChange={(e) => setSelectedWarrantyId(e.target.value)}
            >
              {warrantyList.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.warranty_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            mb: 1,
          }}
        >
          <TextField
            sx={{ ...customTextField, flex: 1 }}
            label="SKU No"
            fullWidth
            type="number"
            size="small"
            id="technician-sku-search"
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddSku();
              }
            }}
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
            }}
          />
          <Button
            variant="outlined"
            disableElevation
            size="small"
            type="button"
            aria-label="Add SKU"
            onClick={handleAddSku}
            disabled={skuLoading}
            sx={{ minWidth: 64, minHeight: 40, px: 1.5, flexShrink: 0 }}
          >
            {skuLoading ? (
              <PulseLoader
                color="#fff"
                loading
                size={6}
                speedMultiplier={0.5}
              />
            ) : (
              "Add"
            )}
          </Button>
          <Button
            variant="outlined"
            color="info"
            disableElevation
            size="small"
            type="button"
            aria-label="Scan SKU"
            onClick={() => openScan("sku")}
            disabled={skuLoading}
            sx={{ minWidth: 40, minHeight: 40, px: 1, flexShrink: 0 }}
          >
            <QrCodeScannerIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box>
        {isWarrantyClaimedSku && (
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              gap: 1,
              mb: 1.5,
            }}
          >
            <TextField
              sx={{ ...customTextField, flex: 1 }}
              label="Claimed on SKU Number"
              fullWidth
              type="number"
              size="small"
              id="technician-claimed-on-sku-search"
              value={claimedOnSkuSearch}
              onChange={(e) =>
                setClaimedOnSkuSearch(e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddSku();
                }
              }}
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
              }}
            />
            <Button
              variant="outlined"
              color="info"
              disableElevation
              size="small"
              type="button"
              aria-label="Scan Claimed on SKU Number"
              onClick={() => openScan("claimed")}
              sx={{ minWidth: 40, minHeight: 40, px: 1, flexShrink: 0 }}
            >
              <QrCodeScannerIcon sx={{ fontSize: 20 }} />
            </Button>
          </Box>
        )}
        {skuList.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {isWarrantyClaimedSku ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                {skuList.map((item) => (
                  <Box
                    key={item.sku_number}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: "8px",
                      background: "#FAFAFA",
                      border: "1px solid #EAECF0",
                    }}
                  >
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        SKU Number
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#0F1624" }}
                      >
                        {item.sku_number}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Claimed on SKU Number
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#0F1624" }}
                      >
                        {item.claimed_on_sku_number}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label="Remove SKU"
                      size="small"
                      onClick={() =>
                        setSkuList((prev) =>
                          prev.filter(
                            (sku) => sku.sku_number !== item.sku_number,
                          ),
                        )
                      }
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                }}
              >
                {skuList.map((item) => (
                  <Chip
                    key={item.sku_number}
                    size="small"
                    label={`${item.sku_number}${
                      item.name ? ` - ${item.name}` : ""
                    }`}
                    onDelete={() =>
                      setSkuList((prev) =>
                        prev.filter(
                          (sku) => sku.sku_number !== item.sku_number,
                        ),
                      )
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="small"
          type="submit"
          disabled={updateLoading}
          sx={{ minHeight: 40, mb: 1.5, fontSize: 14 }}
        >
          {updateLoading === false && "Update"}
          <PulseLoader
            color="#16bb3aff"
            loading={updateLoading}
            size={8}
            speedMultiplier={0.5}
          />
        </Button>
      </Box>

      <Dialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: fullScreen ? 0 : "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#0F1624",
            position: "relative",
            pr: 6,
            borderBottom: "1px solid #EAECF1",
          }}
        >
          {scanTarget === "claimed" ? "Scan Claimed on SKU Number" : "Scan SKU"}
          <IconButton
            aria-label="close"
            onClick={() => setScanOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2 }}>
          {scanOpen && <BarcodeScanner onScan={handleScan} />}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #EAECF1" }}>
          <Button
            variant="outlined"
            color="info"
            onClick={() => setScanOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SparepartsForm;
